import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import Dashboard from './Dashboard.js';
import JobDetailsTab from '../components/JobDetail';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext.js';
import { ROLES } from '../config/routes';

import './JobPostDetail.css';

const JobPostDetail = () => {
  const { selectedCompany } = useCompany();
  const { userRole } = useAuth();
  
  const [activeTab, setActiveTab] = useState('details');
  const [applicants, setApplicants] = useState([]);
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingApplicate, setLoadingApplicate] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalApplicants, setTotalApplicants] = useState(0);
  // const [companyId, setCompanyId] = useState(null);

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const { jobId } = useParams();
  const navigate = useNavigate();

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Authentication token not found');
        setIsUpdatingStatus(false);
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/jobs/status/${jobId}/${newStatus}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: newStatus
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Status updated successfully:', result);
        
        // Update local state
        fetchJobDetails();
        setShowStatusDropdown(false);
                
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const fetchJobDetails = async () => {
    if (!loading) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/jobs/get-jobs/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setJobDetails(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch applicant details');
      }
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch applicant details');
      setLoading(false);
      console.error('Error fetching applicant details:', error);
    }
  };

  const fetchApplicates = async () => {
    if (!loadingApplicate) {
      setLoadingApplicate(true);
    }
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoadingApplicate(false);
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/job-applications/list-applicates/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApplicants(data.items);
        setTotalApplicants(data.total);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch applicant details');
      }
      setLoadingApplicate(false);
    } catch (error) {
      setError('Failed to fetch applicant details');
      setLoadingApplicate(false);
      console.error('Error fetching applicant details:', error);
    }
  };

  useEffect(() => {
    if (selectedCompany || (userRole == ROLES.FUND_MANAGER || userRole == ROLES.ADMIN) ) {
      console.log("fetching data");
      fetchJobDetails();
      if (userRole == ROLES.FUND_MANAGER) {
        fetchApplicates();
      }
    }
  }, [jobId, selectedCompany, navigate]);

  const getStageColor = (stage) => {
    switch (stage.toLowerCase()) {
      case 'hired':
        return 'stage-hired';
      case 'shortlisted':
        return 'stage-shortlisted';
      case 'interview':
        return 'stage-interview';
      case 'declined':
        return 'stage-declined';
      default:
        return 'stage-review';
    }
  };

  const formatStatusDisplay = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getAvailableStatusOptions = (currentStatus) => {
    const statusMap = {
      'active': ['closed', 'draft'],
      'draft': ['active'],
      'closed': []
    };
    return statusMap[currentStatus] || [];
  };

  const formatScore = (score) => {
    return score > 0 ? score.toFixed(1) : '0.0';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCategoryColor = (color) => {
    switch (color) {
      case 'orange':
        return 'category-orange';
      case 'green':
        return 'category-green';
      default:
        return 'category-default';
    }
  };

  const totalPages = Math.ceil(totalApplicants / pageSize);

  const filteredApplicants = applicants.filter(applicant =>
    applicant.user_names.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || loadingApplicate) {
    return <div className="loading-state">Loading job details...</div>;
  }

  if (error) {
    return <div className="loading-state">Errors...</div>;
  }

  return (
    <Dashboard page={"application"}>

    <div className="job-post-detail-container">
      <div className="job-header">
        <div className="header-content">
          <button className="back-btn" onClick={() => navigate(`/listing}`)}>←</button>
          <div className="job-info">
            <h1>{jobDetails.title}</h1>
            <p className="job-meta">
              {jobDetails.job_type} • {jobDetails.status} •  {jobDetails.experience_level}
            </p>
          </div>
          <div className="status-dropdown-container">
                <button className="more-action-btn"
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      disabled={isUpdatingStatus}>
                  {isUpdatingStatus ? 'Updating...' : 'More Action'} ▼
                </button>

                  {showStatusDropdown && (
                      <div className="status-dropdown">
                        <div className="dropdown-header">
                          <span>Current Status: {formatStatusDisplay(jobDetails.status || 'active')}</span>
                        </div>
                        <div className="dropdown-divider"></div>
                        <div className="dropdown-options">
                          {getAvailableStatusOptions(jobDetails.status || 'active').length > 0 ? (
                            getAvailableStatusOptions(jobDetails.status || 'active').map(status => (
                              <button
                                key={status}
                                className="dropdown-option"
                                onClick={() => handleStatusUpdate(status)}
                                disabled={isUpdatingStatus}
                              >
                                Change to {formatStatusDisplay(status)}
                              </button>
                            ))
                          ) : (
                            <div className="no-options">
                              No status changes available
                            </div>
                          )}
                        </div>
                      </div>
                    )}
            </div>

        </div>
      </div>

      <div className="job-tabs">
        { 
          userRole == ROLES.FUND_MANAGER && 
          <button 
            className={`tab ${activeTab === 'applicants' ? 'active' : ''}`}
            onClick={() => setActiveTab('applicants')}>
            Applicants
          </button>
        }
        { 
            (userRole == ROLES.FUND_MANAGER || userRole == ROLES.ADMIN) &&
            <>
              <button 
                className={`tab ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}>
                Job Details
              </button>
              <button 
                className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
                onClick={() => setActiveTab('analytics')}>
                Analytics
              </button>
            </>
        }
      </div>

      {activeTab === 'applicants' && (
        <div className="applicants-tab">
          <div className="applicants-header">
            <h2>Total Applicants: {totalApplicants}</h2>
            <div className="header-actions">
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search Applicants"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <span className="search-icon">🔍</span>
              </div>
              <button className="filter-btn">
                <span className="filter-icon">⚙️</span>
                Filter
              </button>
              {/* <div className="view-toggle">
                <button className="view-btn active">Pipeline View</button>
                <button className="view-btn">Table View</button>
              </div> */}
            </div>
          </div>

          <div className="applicants-table">
            <div className="table-header">
              {/* <div className="header-cell checkbox-cell">
                <input type="checkbox" />
              </div> */}
              <div className="header-cell name-cell">Full Name ↕️</div>
              <div className="header-cell score-cell">Score ↕️</div>
              <div className="header-cell stage-cell">Hiring Stage ↕️</div>
              <div className="header-cell date-cell">Applied Date ↕️</div>
              <div className="header-cell action-cell">Action ↕️</div>
            </div>

            <div className="table-body">
              {filteredApplicants.map((applicant) => (
                <div key={applicant.id} className="table-row">
                  {/* <div className="table-cell checkbox-cell">
                    <input type="checkbox" />
                  </div> */}
                  <div className="table-cell name-cell">
                    <div className="applicant-info">
                      <div className="avatar">
                        <img src={`${process.env.REACT_APP_API_URL}/profile/get-pic/${encodeURIComponent(applicant.user_id)}`} alt={applicant.user_names} />
                      </div>
                      <span className="applicants-table-name">{applicant.user_names}</span>
                    </div>
                  </div>
                  <div className="table-cell score-cell">
                    <div className="score">
                      <span className="star">⭐</span>
                      {formatScore(0)}
                    </div>
                  </div>
                  <div className="table-cell stage-cell">
                    <span className={`stage-badge ${getStageColor(applicant.stages)}`}>
                      {applicant.stages}
                    </span>
                  </div>
                  <div className="table-cell date-cell">
                    {formatDate(applicant.created_at)}
                  </div>
                  <div className="table-cell action-cell">
                    <button 
                    onClick={ () => navigate(`/application-detail/${applicant.application_id}`)}
                    className="see-application-btn">See Application</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pagination">
            <div className="pagination-info">
              <span>View</span>
              <select 
                value={pageSize} 
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="page-size-select"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>Applicants per page</span>
            </div>
            
            <div className="pagination-controls">
              <button 
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'details' && (
        <JobDetailsTab jobData={jobDetails} role={userRole} />
      )}

      {activeTab === 'analytics' && (
        <div className="analytics-tab">
          <p>Analytics content would go here</p>
        </div>
      )}
      </div>
    </Dashboard>
  );
};

export default JobPostDetail;